/* Price On Request: share the reviewed request via WhatsApp. */
(() => {
  'use strict';
  if (window.HOQPriceRequests) {
    window.HOQPriceRequests.mount();
    return;
  }

  const controllers = new Map();
  const STORAGE_KEY = 'hoq.priceRequest.v1';
  const MAX_AGE = 2 * 60 * 60 * 1000;
  const fields = ['name', 'phone', 'email', 'message'];
  const text = (value) => String(value == null ? '' : value);
  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  };
  const readStorage = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      if (value && value.version === 1 && Date.now() - value.at < MAX_AGE) return value;
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* The form also works when storage is blocked. */ }
    return null;
  };

  class QuoteRequest {
    constructor(dialog) {
      this.dialog = dialog;
      this.form = dialog.querySelector('.hoq-rfq__form');
      this.scroll = dialog.querySelector('[data-hoq-scroll]');
      this.picker = dialog.querySelector('[data-hoq-picker]');
      this.nextButton = dialog.querySelector('[data-hoq-next]');
      this.backButton = dialog.querySelector('[data-hoq-back]');
      this.submitButton = dialog.querySelector('[data-hoq-submit]');
      this.root = new URL(dialog.dataset.rootUrl || '/', location.origin).pathname.replace(/\/?$/, '/');
      this.step = 1;
      this.items = [];
      this.busy = false;
      this.submitting = false;
      this.editIndex = null;
      this.sequence = 0;
      this.searchSequence = 0;
      this.requests = new Set();
      this.listeners = new AbortController();
      const signal = this.listeners.signal;
      const draft = readStorage();
      if (draft) {
        this.items = (Array.isArray(draft.items) ? draft.items : []).map((item) => this.safeItem(item)).filter(Boolean);
        fields.forEach((field) => {
          if (typeof draft.fields?.[field] === 'string') this.contact(field).value = draft.fields[field];
        });
      }

      dialog.addEventListener('click', (event) => this.click(event), {signal});
      dialog.addEventListener('change', (event) => this.change(event), {signal});
      dialog.addEventListener('input', (event) => {
        if (event.target.matches('[data-hoq-search]')) {
          clearTimeout(this.searchTimer);
          this.searchSequence++;
          this.sequence++;
          this.selected = null;
          this.picker.querySelector('[data-hoq-selection]').hidden = true;
          this.searchTimer = setTimeout(() => this.search(), 250);
        }
        if (event.target.matches('[data-hoq-contact]')) {
          event.target.setCustomValidity('');
          clearTimeout(this.saveTimer);
          this.saveTimer = setTimeout(() => this.save(), 180);
        }
      }, {signal});
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-hoq-search]')) {
          event.preventDefault();
          clearTimeout(this.searchTimer);
          this.search();
        }
      }, {signal});
      dialog.addEventListener('close', () => this.afterClose(), {signal});
      this.form.addEventListener('submit', (event) => this.submit(event), {signal});
      window.addEventListener('pageshow', () => this.resetSubmitting(), {signal});
      this.renderItems();

      // Moving the dialog avoids clipped overlays inside sticky/animated product containers.
      document.body.appendChild(dialog);

    }

    contact(field) { return this.dialog.querySelector(`[data-hoq-contact="${field}"]`); }

    productURL(value) {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) throw new Error('Invalid product URL');
      if (this.root !== '/' && url.pathname.startsWith('/products/')) url.pathname = this.root + url.pathname.slice(1);
      url.search = '';
      url.hash = '';
      return url;
    }

    imageURL(value) {
      if (!value) return '';
      try {
        const url = new URL(value, location.origin);
        return url.protocol === 'https:' || url.origin === location.origin ? url.href : '';
      } catch (_) { return ''; }
    }

    safeItem(item) {
      try {
        if (!item || !/^\d+$/.test(text(item.productId)) || !/^\d+$/.test(text(item.variantId))) return null;
        const properties = Object.fromEntries(Object.entries(item.properties || {}).filter(([k,v]) => typeof v === 'string' && k.length < 150).map(([k,v]) => [k,v.slice(0,2000)]));
        return {
          productId: text(item.productId), variantId: text(item.variantId),
          title: text(item.title).slice(0,500), variant: text(item.variant).slice(0,500), sku: text(item.sku).slice(0,250),
          url: this.productURL(item.url).href, image: this.imageURL(item.image),
          quantity: Math.max(1,Math.min(9999,parseInt(item.quantity,10) || 1)), properties
        };
      } catch (_) { return null; }
    }

    save() {
      const values = Object.fromEntries(fields.map((key) => [key,this.contact(key).value]));
      try {
        sessionStorage.setItem(STORAGE_KEY,JSON.stringify({version:1,at:Date.now(),items:this.items,fields:values}));
      } catch (_) { /* No storage permission is required for submission. */ }
    }

    status(message, error = false) {
      const node = this.dialog.querySelector('[data-hoq-status]');
      node.textContent = message;
      node.hidden = !message;
      node.classList.toggle('hoq-rfq__notice--error',error);
    }

    show() {
      if (this.dialog.open) return;
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
    }

    afterClose() {
      this.sequence++;
      this.searchSequence++;
      this.requests.forEach((request) => request.abort());
      clearTimeout(this.searchTimer);
      this.busy = false;
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.save();
      if (this.opener?.isConnected) this.opener.focus({preventScroll:true});
    }

    destroy() {
      if (this.dialog.open) this.dialog.close();
      this.requests.forEach((request) => request.abort());
      this.listeners.abort();
      clearTimeout(this.saveTimer);
      clearTimeout(this.searchTimer);
      this.dialog.remove();
    }

    selectionFromPage(trigger) {
      const form = document.getElementById(trigger.dataset.hoqProductForm);
      const scope = form?.closest('product-info, product-component') || document.getElementById('shopify-section-' + trigger.dataset.hoqSection);
      const picker = scope?.querySelector('variant-selects');
      let ids = [];
      try {
        if (Array.isArray(picker?.selectedOptionValues)) ids = picker.selectedOptionValues;
      } catch (_) { /* Use the actual selected controls below. */ }
      if (!ids.length && picker) {
        ids = Array.from(picker.querySelectorAll('select')).map((select) => select.selectedOptions[0]?.dataset.optionValueId).filter(Boolean);
        if (!ids.length) ids = Array.from(picker.querySelectorAll('input:checked, [data-popout-input]')).map((input) => input.dataset.optionValueId).filter(Boolean);
      }
      const idInput = form?.querySelector('[name="id"]');
      const variantId = idInput ? idInput.value : trigger.dataset.hoqVariant;
      if (!ids.length && !/^\d+$/.test(variantId || '')) throw new Error('Choose a product option combination first.');
      const quantityInput = form?.elements.namedItem('quantity');
      const quantity = Math.max(1,Math.min(9999,parseInt(quantityInput?.value,10) || 1));
      const properties = {};
      if (form) {
        for (const [key,value] of new FormData(form)) {
          const match = /^properties\[(.+)\]$/.exec(key);
          if (match && typeof value === 'string' && value.trim()) Object.defineProperty(properties,match[1],{value,enumerable:true,configurable:true});
        }
      }
      return {params:ids.length ? {option_values:ids.join(',')} : {variant:variantId}, quantity, properties};
    }

    async fetchProduct(value, params = {}) {
      const url = this.productURL(value);
      url.searchParams.set('section_id','hoq-price-request-data');
      Object.entries(params).forEach(([key,val]) => url.searchParams.set(key,val));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id',preview);
      const controller = new AbortController();
      this.requests.add(controller);
      const timeout = setTimeout(() => controller.abort(),15000);
      try {
        const response = await fetch(url.href,{credentials:'same-origin',signal:controller.signal,headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Could not load product');
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        const node = html.querySelector('[data-hoq-product-data]');
        if (!node) throw new Error('Product data section missing');
        const data = JSON.parse(node.textContent);
        if (!data?.id || !data.url) throw new Error('Product not found');
        return data;
      } finally {
        clearTimeout(timeout);
        this.requests.delete(controller);
      }
    }

    async open(trigger) {
      this.opener = trigger;
      this.resetSubmitting();
      this.dialog.querySelector('.hoq-rfq__steps').hidden = false;
      this.closePicker(false);
      this.show();
      this.setStep(1);
      this.busy = true;
      this.nextButton.disabled = true;
      this.status('Loading your selected product…');
      const sequence = ++this.sequence;
      try {
        const selection = this.selectionFromPage(trigger);
        const data = await this.fetchProduct(trigger.dataset.hoqProductUrl,selection.params);
        if (sequence !== this.sequence) return;
        if (!data.variant) throw new Error('This option combination does not exist. Please select another combination on the product page.');
        const item = this.makeItem(data,selection.quantity,selection.properties);
        this.addItem(item,false);
        this.status('');
      } catch (error) {
        if (sequence !== this.sequence) return;
        this.status(error.message.startsWith('This option') || error.message.startsWith('Choose a') ? error.message : 'Unable to load the selected product. Close this window and try again.',true);
      } finally {
        if (sequence === this.sequence) {
          this.busy = false;
          this.nextButton.disabled = false;
          this.save();
        }
      }
    }

    makeItem(data,quantity,properties = {}) {
      const item = this.safeItem({productId:data.id,variantId:data.variant?.id,title:data.title,variant:data.hasOnlyDefaultVariant ? '' : data.variant?.title,sku:data.variant?.sku,url:data.url,image:data.image,quantity,properties});
      if (!item) throw new Error('Invalid product selection');
      return item;
    }

    itemKey(item) {
      return JSON.stringify([item.productId,item.variantId,Object.entries(item.properties).sort(([a],[b]) => a.localeCompare(b))]);
    }

    addItem(item,increment = true) {
      const existing = this.items.find((row) => this.itemKey(row) === this.itemKey(item));
      if (existing) {
        if (increment) existing.quantity = Math.min(9999,existing.quantity + item.quantity);
        existing.title = item.title;
        existing.variant = item.variant;
        existing.image = item.image;
        existing.sku = item.sku;
      } else this.items.push(item);
      this.renderItems();
      this.save();
    }

    image(item) {
      const url = this.imageURL(item.image);
      if (!url) return el('span','hoq-rfq__image-empty');
      const node = el('img','hoq-rfq__image');
      node.src = url;
      node.alt = item.title;
      node.width = 74;
      node.height = 74;
      node.loading = 'lazy';
      return node;
    }

    renderItems() {
      const editable = this.dialog.querySelector('[data-hoq-items]');
      const review = this.dialog.querySelector('[data-hoq-review-items]');
      editable.replaceChildren();
      review.replaceChildren();
      if (!this.items.length) editable.append(el('p','hoq-rfq__empty','No products selected. Add a product to request a quote.'));
      this.items.forEach((item,index) => {
        [false,true].forEach((readOnly) => {
          const row = el('div','hoq-rfq__item');
          const product = el('div','hoq-rfq__product');
          const detail = el('div','hoq-rfq__product-details');
          const title = el(readOnly ? 'span' : 'button','hoq-rfq__product-title',item.title);
          if (!readOnly) {
            title.type = 'button';
            title.dataset.hoqEdit = index;
            title.setAttribute('aria-label','Edit options for ' + item.title);
          }
          detail.append(title);
          if (item.variant) detail.append(el('p','hoq-rfq__variant',item.variant));
          const properties = Object.entries(item.properties).map(([key,value]) => key + ': ' + value).join('\n');
          if (properties) detail.append(el('p','hoq-rfq__properties',properties));
          product.append(this.image(item),detail);
          row.append(product);
          if (readOnly) row.append(el('div','hoq-rfq__read-quantity',text(item.quantity)));
          else {
            const controls = el('div','hoq-rfq__quantity');
            const input = el('input','hoq-rfq__quantity-input');
            Object.assign(input,{type:'number',min:'1',max:'9999',step:'1',value:text(item.quantity),required:true});
            input.inputMode = 'numeric';
            input.dataset.hoqQuantity = index;
            input.setAttribute('aria-label','Quantity for ' + item.title + (item.variant ? ' — ' + item.variant : ''));
            const remove = el('button','hoq-rfq__remove');
            remove.type = 'button';
            remove.dataset.hoqRemove = index;
            remove.setAttribute('aria-label','Remove ' + item.title);
            remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
            controls.append(input,remove);
            row.append(controls);
          }
          (readOnly ? review : editable).append(row);
        });
      });
    }

    click(event) {
      const button = event.target.closest('button');
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
      if (!button || !this.dialog.contains(button)) return;
      if (this.busy && !button.matches('[data-hoq-close], [data-hoq-back]')) return;
      if (button.matches('[data-hoq-close]')) this.dialog.close();
      if (button.matches('[data-hoq-back]')) {
        if (this.step === 1) this.dialog.close();
        else this.setStep(this.step - 1);
      }
      if (button.matches('[data-hoq-next]')) this.next();
      if (button.matches('[data-hoq-picker-open]')) this.openPicker();
      if (button.matches('[data-hoq-picker-close]')) this.closePicker();
      if (button.matches('[data-hoq-remove]')) {
        this.items.splice(Number(button.dataset.hoqRemove),1);
        this.closePicker(false);
        this.renderItems();
        this.save();
        this.dialog.querySelector('[data-hoq-picker-open]').focus();
      }
      if (button.matches('[data-hoq-edit]')) this.openPicker(Number(button.dataset.hoqEdit));
      if (button.matches('[data-hoq-select-product]')) {
        this.editIndex = null;
        this.picker.querySelectorAll('[data-hoq-select-product]').forEach((row) => row.setAttribute('aria-pressed',text(row === button)));
        this.loadSelection(button.dataset.hoqSelectProduct);
      }
      if (button.matches('[data-hoq-add-selected]')) this.commitSelection();
    }

    change(event) {
      const node = event.target;
      if (node.matches('[data-hoq-quantity]')) {
        const item = this.items[Number(node.dataset.hoqQuantity)];
        if (item && node.checkValidity()) {
          item.quantity = Number(node.value);
          this.save();
        } else node.reportValidity();
      }
      if (node.matches('[data-hoq-option]')) {
        const selects = Array.from(this.picker.querySelectorAll('[data-hoq-option]'));
        const ids = selects.map((select) => select.value);
        const sibling = node.selectedOptions[0]?.dataset.productUrl;
        this.loadSelection(sibling || this.selected.url,{option_values:ids.join(',')},Number(node.dataset.hoqOption));
      }
    }

    setStep(step) {
      this.step = step;
      this.status('');
      this.dialog.querySelectorAll('[data-hoq-panel]').forEach((panel) => panel.hidden = Number(panel.dataset.hoqPanel) !== step);
      this.dialog.querySelectorAll('[data-hoq-step-label]').forEach((label) => {
        if (Number(label.dataset.hoqStepLabel) === step) label.setAttribute('aria-current','step');
        else label.removeAttribute('aria-current');
      });
      this.backButton.textContent = step === 1 ? 'Continue Shopping' : 'Previous step';
      this.nextButton.hidden = step === 3;
      this.submitButton.hidden = step !== 3;
      this.scroll.scrollTop = 0;
      this.dialog.querySelector(`[data-hoq-panel="${step}"] [data-hoq-panel-focus]`)?.focus({preventScroll:true});
    }

    validateProducts() {
      if (this.busy) return false;
      if (!this.items.length) {
        this.setStep(1);
        this.status('Please add at least one product to your quote request.',true);
        return false;
      }
      const inputs = Array.from(this.dialog.querySelectorAll('[data-hoq-quantity]'));
      const invalid = inputs.find((input) => !input.checkValidity());
      if (invalid) {
        this.setStep(1);
        invalid.reportValidity();
        return false;
      }
      inputs.forEach((input) => this.items[Number(input.dataset.hoqQuantity)].quantity = Number(input.value));
      return true;
    }

    validateContact() {
      for (const key of fields) {
        const input = this.contact(key);
        input.value = input.value.trim();
        input.setCustomValidity('');
        if (key === 'phone' && input.value && !/^\+?[\d\s().-]+$/.test(input.value)) input.setCustomValidity('Please enter a valid phone number.');
        if (key === 'phone' && input.value) {
          const length = input.value.replace(/\D/g,'').length;
          if (length < 7 || length > 15) input.setCustomValidity('Please enter a phone number with 7 to 15 digits.');
        }
        if (!input.checkValidity()) {
          this.setStep(2);
          this.dialog.querySelector('.hoq-rfq__extra').open = true;
          input.reportValidity();
          return false;
        }
      }
      return true;
    }

    next() {
      if (this.busy) return;
      if (this.step === 1) {
        if (!this.picker.hidden) {
          this.status('Add the selected product or choose Done before continuing.',true);
          this.scroll.scrollTop = 0;
          return;
        }
        if (!this.validateProducts()) return;
        this.setStep(2);
      } else if (this.step === 2) {
        if (!this.validateProducts() || !this.validateContact()) return;
        this.renderItems();
        fields.forEach((key) => this.dialog.querySelector(`[data-hoq-review="${key}"]`).textContent = this.contact(key).value);
        this.setStep(3);
      }
      this.save();
    }

    openPicker(index = null) {
      this.status('');
      this.editIndex = index;
      this.picker.hidden = false;
      this.selected = null;
      this.picker.querySelector('[data-hoq-selection]').hidden = true;
      this.picker.querySelector('[data-hoq-search-results]').replaceChildren();
      const input = this.picker.querySelector('[data-hoq-search]');
      input.value = '';
      input.closest('label').hidden = index !== null;
      const status = this.picker.querySelector('[data-hoq-search-status]');
      status.textContent = index === null ? 'Type at least 2 characters to search.' : '';
      this.picker.querySelector('[data-hoq-picker-title]').textContent = index === null ? 'Add products to group' : 'Edit product options';
      this.picker.querySelector('[data-hoq-picker-quantity]').value = index === null ? '1' : text(this.items[index].quantity);
      this.picker.querySelector('[data-hoq-add-selected]').textContent = index === null ? 'Add to quote' : 'Save changes';
      this.picker.scrollIntoView({block:'start'});
      if (index === null) input.focus({preventScroll:true});
      else this.loadSelection(this.items[index].url,{variant:this.items[index].variantId});
    }

    closePicker(focus = true) {
      this.sequence++;
      this.searchSequence++;
      clearTimeout(this.searchTimer);
      this.picker.hidden = true;
      this.selected = null;
      this.editIndex = null;
      if (focus) this.dialog.querySelector('[data-hoq-picker-open]').focus({preventScroll:true});
    }

    async search() {
      const query = this.picker.querySelector('[data-hoq-search]').value.trim();
      const status = this.picker.querySelector('[data-hoq-search-status]');
      const results = this.picker.querySelector('[data-hoq-search-results]');
      const sequence = ++this.searchSequence;
      results.replaceChildren();
      if (query.length < 2) {
        status.textContent = 'Type at least 2 characters to search.';
        return;
      }
      status.textContent = 'Searching products…';
      const url = new URL(this.root + 'search/suggest.json',location.origin);
      url.searchParams.set('q',query);
      url.searchParams.set('resources[type]','product');
      url.searchParams.set('resources[limit]','10');
      url.searchParams.set('resources[options][unavailable_products]','show');
      const controller = new AbortController();
      this.requests.add(controller);
      const timeout = setTimeout(() => controller.abort(),15000);
      try {
        const response = await fetch(url.href,{credentials:'same-origin',signal:controller.signal});
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (sequence !== this.searchSequence) return;
        const products = data.resources?.results?.products || [];
        status.textContent = products.length ? 'Select a product to choose its options.' : 'No matching products. Try another product name.';
        products.forEach((product) => {
          let url;
          try { url = this.productURL(product.url); } catch (_) { return; }
          const button = el('button','hoq-rfq__search-result');
          button.type = 'button';
          button.dataset.hoqSelectProduct = url.href;
          button.setAttribute('aria-pressed','false');
          const image = this.imageURL(product.featured_image?.url || product.image);
          if (image) {
            const img = el('img');
            Object.assign(img,{src:image,alt:'',width:48,height:48,loading:'lazy'});
            button.append(img);
          }
          button.append(el('span','',text(product.title)));
          results.append(button);
        });
      } catch (_) {
        if (sequence === this.searchSequence) status.textContent = 'Product search is temporarily unavailable. Please try again.';
      } finally {
        clearTimeout(timeout);
        this.requests.delete(controller);
      }
    }

    async loadSelection(url,params = {},focusPosition = null) {
      const sequence = ++this.sequence;
      const holder = this.picker.querySelector('[data-hoq-selection]');
      const status = this.picker.querySelector('[data-hoq-option-status]');
      const add = this.picker.querySelector('[data-hoq-add-selected]');
      holder.hidden = false;
      add.disabled = true;
      status.textContent = 'Loading product options…';
      this.picker.querySelectorAll('[data-hoq-option]').forEach((select) => select.disabled = true);
      this.selected = null;
      try {
        const data = await this.fetchProduct(url,params);
        if (sequence !== this.sequence) return;
        this.selected = data;
        this.picker.querySelector('[data-hoq-selection-title]').textContent = data.title;
        const options = this.picker.querySelector('[data-hoq-options]');
        options.replaceChildren();
        if (!data.hasOnlyDefaultVariant) {
          data.options.forEach((option) => {
            const label = el('label','hoq-rfq__field');
            label.append(el('span','',option.name));
            const select = el('select');
            select.dataset.hoqOption = option.position;
            option.values.forEach((value) => {
              const node = el('option','',value.name);
              node.value = value.id;
              node.selected = value.selected || value.name === option.selectedValue;
              if (value.productUrl) node.dataset.productUrl = value.productUrl;
              select.append(node);
            });
            label.append(select);
            options.append(label);
          });
        }
        status.textContent = data.variant ? '' : 'This option combination does not exist. Please choose another combination.';
        add.disabled = !data.variant;
        if (focusPosition != null) options.querySelector(`[data-hoq-option="${focusPosition}"]`)?.focus({preventScroll:true});
      } catch (_) {
        if (sequence === this.sequence) status.textContent = 'Unable to load these product options. Please select the product again.';
      }
    }

    commitSelection() {
      if (!this.selected?.variant) return;
      const quantity = this.picker.querySelector('[data-hoq-picker-quantity]');
      if (!quantity.value || !quantity.reportValidity()) return;
      const previous = this.editIndex === null ? null : this.items[this.editIndex];
      const item = this.makeItem(this.selected,Number(quantity.value),previous?.properties || {});
      if (previous) {
        const duplicate = this.items.find((row,index) => index !== this.editIndex && this.itemKey(row) === this.itemKey(item));
        if (duplicate) {
          duplicate.quantity = Math.min(9999,duplicate.quantity + item.quantity);
          this.items.splice(this.editIndex,1);
        } else this.items.splice(this.editIndex,1,item);
        this.renderItems();
        this.save();
      } else this.addItem(item);
      this.closePicker();
      this.status('');
      this.scroll.scrollTop = 0;
    }

    submit(event) {
      event.preventDefault();
      if (this.step !== 3) {
        event.preventDefault();
        this.next();
        return;
      }
      if (this.submitting || !this.validateProducts() || !this.validateContact()) {
        event.preventDefault();
        return;
      }
      const summary = this.items.map((item,index) => {
        const url = this.productURL(item.url);
        url.searchParams.set('variant',item.variantId);
        return [
          (index + 1) + '. ' + item.title,
          'Variant: ' + (item.variant || 'Default'),
          'SKU: ' + (item.sku || 'Not set'),
          'Quantity: ' + item.quantity,
          'Product ID: ' + item.productId,
          'Variant ID: ' + item.variantId,
          ...Object.entries(item.properties).map(([key,value]) => key + ': ' + value),
          'Product link: ' + url.href
        ].join('\n');
      }).join('\n\n');
      const source = new URL(location.href);
      source.hash = '';
      // Keep the product selection, without email-campaign or preview query strings.
      const selectedVariant = source.searchParams.get('variant');
      source.search = '';
      if (selectedVariant) source.searchParams.set('variant',selectedVariant);
      const number = (this.dialog.dataset.whatsapp || '').replace(/\D/g,'');
      if (!number) {
        this.status('WhatsApp is unavailable. Please contact our team.',true);
        return;
      }
      const message = [
        'Hi, I would like to request a price quote.',
        ...fields.map((key) => key.charAt(0).toUpperCase() + key.slice(1) + ': ' + this.contact(key).value),
        '', 'Products requested:', summary, '', 'Source page: ' + source.href
      ].join('\n');
      this.save();
      window.open('https://wa.me/' + number + '?text=' + encodeURIComponent(message), '_blank', 'noopener');
      this.status('Tap Send in WhatsApp to share your quote request with our team.');
    }

    resetSubmitting() {
      this.submitting = false;
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Continue on WhatsApp';
    }

  }

  function mount() {
    document.querySelectorAll('[data-hoq-dialog]').forEach((dialog) => {
      if (!dialog.isConnected) return;
      const existing = controllers.get(dialog.id);
      if (existing?.dialog === dialog) return;
      if (existing) existing.destroy();
      controllers.set(dialog.id,new QuoteRequest(dialog));
    });
  }

  window.HOQPriceRequests = {mount};
  document.addEventListener('click',(event) => {
    const trigger = event.target.closest('[data-hoq-open]');
    if (!trigger) return;
    event.preventDefault();
    mount();
    controllers.get(trigger.dataset.hoqOpen)?.open(trigger);
  });
  document.addEventListener('shopify:section:load',mount);
  document.addEventListener('shopify:section:unload',(event) => {
    const id = 'HOQPriceRequest-' + event.detail.sectionId;
    const controller = controllers.get(id);
    if (controller) { controller.destroy(); controllers.delete(id); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',mount,{once:true});
  else mount();
})();
